import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ArrowDownUp, Printer } from 'lucide-react';
import ClientsTable from './ClientsTable';
import Modal from '../ui/Modal';
import { printClientIntakeForm } from '../../utils/printRegistrationForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Client Calendar Modal Component
const ClientCalendarModal = ({ client, onClose }) => {
  const [clientData, setClientData] = useState({
    workouts: [],
    bookings: [],
    externalWorkouts: []
  });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [currentMonth, setCurrentMonth] = useState(new Date());

  useEffect(() => {
    fetchClientCalendarData();
  }, [client.id]);

  const fetchClientCalendarData = async () => {
    try {
      setLoading(true);
      const [workoutsRes, bookingsRes, externalRes] = await Promise.all([
        axios.get(`${API}/workouts?client_id=${client.id}`),
        axios.get(`${API}/bookings?client_id=${client.id}`),
        axios.get(`${API}/external-workouts?client_id=${client.id}`)
      ]);
      
      setClientData({
        workouts: workoutsRes.data || [],
        bookings: Array.isArray(bookingsRes.data) 
          ? bookingsRes.data 
          : (bookingsRes.data?.bookings || []),
        externalWorkouts: externalRes.data || []
      });
    } catch (error) {
      console.error('Error fetching client calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const validBookingIds = useMemo(() => {
    return new Set(clientData.bookings.map(b => b.id));
  }, [clientData.bookings]);

  const workoutByBookingId = useMemo(() => {
    const map = new Map();
    clientData.workouts
      .filter(w => w.booking_id && validBookingIds.has(w.booking_id))
      .forEach(w => map.set(w.booking_id, w));
    return map;
  }, [clientData.workouts, validBookingIds]);

  const allActivities = useMemo(() => {
    const activities = [];
    
    if (filter === 'all' || filter === 'workouts') {
      clientData.bookings.forEach(b => {
        const workout = workoutByBookingId.get(b.id);
        activities.push({
          id: b.id,
          type: 'booking',
          date: b.date || '',
          title: 'Training Booking',
          status: b.status,
          details: `${b.start_time} - ${b.end_time}`,
          hasWorkout: !!workout,
          workout: workout,
          workoutStatus: workout?.status,
          exerciseCount: workout?.exercises?.length || 0,
          duration: workout?.duration || 0,
          totalTime: workout?.total_time || 0,
          raw: b
        });
      });
    }
    
    if (filter === 'all' || filter === 'external') {
      clientData.externalWorkouts.forEach(e => {
        activities.push({
          id: e.id,
          type: 'external',
          date: e.date || '',
          title: e.activity_type?.replace('_', ' ').toUpperCase() || 'External Activity',
          status: 'completed',
          details: `${e.duration} min`,
          duration: e.duration,
          notes: e.notes,
          raw: e
        });
      });
    }
    
    return activities.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [clientData, filter, workoutByBookingId]);

  const activitiesByDate = useMemo(() => {
    const grouped = {};
    allActivities.forEach(activity => {
      const date = activity.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(activity);
    });
    return grouped;
  }, [allActivities]);

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    
    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  };

  const getActivitiesForDay = (day) => {
    if (!day) return [];
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return activitiesByDate[dateStr] || [];
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() && 
           currentMonth.getMonth() === today.getMonth() && 
           currentMonth.getFullYear() === today.getFullYear();
  };

  const stats = useMemo(() => {
    const bookingsWithWorkout = clientData.bookings.filter(b => workoutByBookingId.has(b.id));
    const completedWorkouts = bookingsWithWorkout.filter(b => {
      const w = workoutByBookingId.get(b.id);
      return w?.status === 'completed';
    }).length;
    
    const totalBookings = clientData.bookings.length;
    const totalExternalActivities = clientData.externalWorkouts.length;
    
    const totalWorkoutTime = Array.from(workoutByBookingId.values())
      .reduce((sum, w) => sum + (w.total_time || w.duration || 0), 0);
    const totalExternalTime = clientData.externalWorkouts.reduce((sum, e) => sum + (e.duration || 0), 0);
    
    return {
      totalBookings,
      bookingsWithWorkout: bookingsWithWorkout.length,
      completedWorkouts,
      totalExternalActivities,
      totalTrainingTime: totalWorkoutTime + totalExternalTime
    };
  }, [clientData, workoutByBookingId]);

  return (
    <Modal onClose={onClose} maxWidth="max-w-6xl">
      <Modal.Header onClose={onClose}>
        <div>
          <h3 className="text-xl font-semibold text-white">
            {client.full_name}&apos;s Calendar
          </h3>
          <p className="text-blue-300 text-sm">Training history for this client</p>
        </div>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center py-12">
            <p className="text-blue-200">Loading calendar data...</p>
          </div>
        ) : (
          <>
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              <div className="bg-blue-900/30 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-white">{stats.totalBookings}</div>
                <div className="text-blue-300 text-xs">Bookings</div>
              </div>
              <div className="bg-green-900/30 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-white">{stats.bookingsWithWorkout}</div>
                <div className="text-green-300 text-xs">With Workout</div>
              </div>
              <div className="bg-orange-900/30 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-white">{stats.totalExternalActivities}</div>
                <div className="text-orange-300 text-xs">External Activities</div>
              </div>
              <div className="bg-purple-900/30 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-white">{stats.totalTrainingTime}</div>
                <div className="text-purple-300 text-xs">Total Minutes</div>
              </div>
              <div className="bg-yellow-900/30 p-3 rounded-lg text-center">
                <div className="text-2xl font-bold text-white">{allActivities.length}</div>
                <div className="text-yellow-300 text-xs">Total Activities</div>
              </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filter === 'all' ? 'bg-blue-500 text-white' : 'bg-blue-900/30 text-blue-200 hover:bg-blue-800/40'
                }`}
              >
                All Activities
              </button>
              <button
                onClick={() => setFilter('workouts')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filter === 'workouts' ? 'bg-green-500 text-white' : 'bg-green-900/30 text-green-200 hover:bg-green-800/40'
                }`}
              >
                Bookings Only
              </button>
              <button
                onClick={() => setFilter('external')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  filter === 'external' ? 'bg-orange-500 text-white' : 'bg-orange-900/30 text-orange-200 hover:bg-orange-800/40'
                }`}
              >
                External Only
              </button>
            </div>

            {/* Calendar View */}
            <div className="glass-card p-4 mb-4">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold text-white">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h4>
                <div className="flex gap-2">
                  <button onClick={() => navigateMonth(-1)} className="btn-secondary text-sm">←</button>
                  <button onClick={() => navigateMonth(1)} className="btn-secondary text-sm">→</button>
                </div>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                  <div key={day} className="text-center text-blue-300 font-medium text-xs py-1">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {getDaysInMonth().map((day, index) => {
                  const dayActivities = getActivitiesForDay(day);
                  const hasActivity = dayActivities.length > 0;
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-[60px] p-1 rounded text-xs ${
                        day 
                          ? `${isToday(day) ? 'ring-2 ring-yellow-400' : ''} ${
                              hasActivity ? 'bg-blue-600/30' : 'bg-blue-900/10'
                            }`
                          : ''
                      }`}
                    >
                      {day && (
                        <>
                          <div className="text-white font-semibold mb-1">{day}</div>
                          <div className="flex flex-col gap-0.5">
                            {dayActivities.slice(0, 3).map((activity, idx) => (
                              <div 
                                key={idx}
                                className={`text-[10px] px-1 py-0.5 rounded truncate ${
                                  activity.type === 'booking' 
                                    ? (activity.hasWorkout ? 'bg-green-500/40 text-green-200' : 'bg-blue-500/40 text-blue-200')
                                    : 'bg-orange-500/40 text-orange-200'
                                }`}
                              >
                                {activity.type === 'booking' 
                                  ? (activity.hasWorkout ? '🏋️' : '📅')
                                  : '🏃'}
                              </div>
                            ))}
                            {dayActivities.length > 3 && (
                              <div className="text-[10px] text-blue-300">+{dayActivities.length - 3}</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Activity List */}
            <div className="glass-card p-4">
              <h4 className="text-lg font-semibold text-white mb-3">Activity History</h4>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {allActivities.length === 0 ? (
                  <p className="text-blue-300 text-center py-4">No activities found with current filter</p>
                ) : (
                  allActivities.map((activity) => (
                    <div 
                      key={`${activity.type}-${activity.id}`}
                      className={`p-3 rounded-lg border ${
                        activity.type === 'booking' 
                          ? (activity.hasWorkout ? 'bg-green-900/20 border-green-400/30' : 'bg-blue-900/20 border-blue-400/30')
                          : 'bg-orange-900/20 border-orange-400/30'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">
                              {activity.type === 'booking' 
                                ? (activity.hasWorkout ? '🏋️' : '📅')
                                : '🏃'}
                            </span>
                            <span className="text-white font-medium">
                              {activity.type === 'booking' 
                                ? (activity.hasWorkout ? 'Workout' : 'Booking (no workout)')
                                : activity.title}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              activity.status === 'completed' ? 'bg-green-500/30 text-green-200' :
                              activity.status === 'cancelled' || activity.status === 'rejected' ? 'bg-red-500/30 text-red-200' :
                              activity.status === 'approved' ? 'bg-blue-500/30 text-blue-200' :
                              'bg-yellow-500/30 text-yellow-200'
                            }`}>
                              {activity.status}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 text-sm">
                            <span className="text-blue-300">{activity.date}</span>
                            <span className="text-blue-200">{activity.details}</span>
                            {activity.hasWorkout && activity.exerciseCount > 0 && (
                              <span className="text-green-300">{activity.exerciseCount} exercises</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

const ClientManager = ({ clients, onRefresh, userRole, t }) => {
  const [reordering, setReordering] = useState(false);
  const isAdmin = userRole === 'administrator';

  const handleReorder = async () => {
    const ok = window.confirm(
      'Reassign ALL member numbers by registration date?\n\n' +
      'The earliest-registered client becomes #1, the next #2, and so on. ' +
      'This overwrites current member numbers and cannot be undone.\n\n' +
      'Make sure registration dates are correct first.'
    );
    if (!ok) return;
    try {
      setReordering(true);
      const res = await axios.post(`${API}/users/reorder-member-numbers`);
      alert(`Done. ${res.data.changed} of ${res.data.reordered} member numbers updated.`);
      if (onRefresh) onRefresh();
    } catch (error) {
      alert('Failed to reorder: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setReordering(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-white">Client Management</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => printClientIntakeForm(null)}
            data-testid="print-blank-intake-btn"
            className="px-3 py-2 bg-blue-500/20 text-blue-200 rounded-lg hover:bg-blue-500/30 transition text-sm font-medium flex items-center gap-2"
            title="Imprimir una ficha de alta en blanco para rellenar a mano"
          >
            <Printer className="w-4 h-4" />
            Imprimir ficha en blanco
          </button>
          {isAdmin && (
            <button
              onClick={handleReorder}
              disabled={reordering}
              data-testid="reorder-member-numbers-btn"
              className="px-3 py-2 bg-purple-500/20 text-purple-200 rounded-lg hover:bg-purple-500/30 transition text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              title="Reassign member numbers in registration-date order"
            >
              <ArrowDownUp className="w-4 h-4" />
              {reordering ? 'Reordering…' : 'Reorder by registration date'}
            </button>
          )}
        </div>
      </div>
      
      <ClientsTable 
        clients={clients}
        t={t}
      />
    </div>
  );
};

export default ClientManager;
